import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  NgxMaskDirective,
  provideEnvironmentNgxMask,
  provideNgxMask,
} from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { UiSecretsService } from './services/ui-secrets.service';
import { GoogleMapsLoaderService } from './services/google-maps-loader.service';
import { UI_SECRET_KEYS } from './constants/ui-secret-keys';
import { getStoredUser } from './helpers/user-storage.util';
import { sessionExpiredInterceptor } from './helpers/session-expired.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    // Only this interceptor: TokenInterceptor stays unwired, because it re-sends
    // every failed request that is not a 401.
    provideHttpClient(withInterceptors([sessionExpiredInterceptor])),
    provideEnvironmentNgxMask(),
    NgxMaskDirective,
    provideNgxMask(),
    ToastrModule,
    provideAnimations(), // Required for ngx-toastr
    importProvidersFrom(
      ToastrModule.forRoot({
        timeOut: 5000, // Duration of toast
        positionClass: 'toast-top-center', // Position of toast
        preventDuplicates: true, // Prevent duplicate toasts
      })
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: (
        uiSecrets: UiSecretsService,
        mapsLoader: GoogleMapsLoaderService
      // Initializes app secrets and loads Google Maps at startup.
      // Injected services are provided via deps array below.
      // Fails fast on error to prevent app from running with missing configuration.
      ) => {
        return () => {
          // Internal users have no map-based features; skip the secrets fetch
          // and Google Maps load entirely so no network call is made on their
          // behalf at startup.
          if (getStoredUser()?.role === 'INTERNAL') {
            return Promise.resolve();
          }
          return firstValueFrom(uiSecrets.fetchSecrets())
            .then((secrets) => {
              if (secrets?.[UI_SECRET_KEYS.GOOGLE_MAPS_API_KEY]) {
                return mapsLoader.load(secrets[UI_SECRET_KEYS.GOOGLE_MAPS_API_KEY]);
              }
              return;
            })
            .catch((error) => { throw error; });
        };
      },
      deps: [UiSecretsService, GoogleMapsLoaderService],
      multi: true,
    },
  ],
};
